import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';

const EMPTY_FORM = {
  code: '',
  discountType: 'percent',
  discountValue: '10',
  minSubtotal: '0',
  maxDiscount: '',
  startsAt: '',
  endsAt: '',
  firstOrderOnly: false,
  usageLimit: '',
  perUserLimit: '1',
  active: true,
};

const toDateInput = (value) => value ? new Date(value).toISOString().slice(0, 16) : '';

function formFromPromo(promo) {
  return {
    code: promo.code || '',
    discountType: promo.discountType || 'percent',
    discountValue: String(promo.discountValue ?? ''),
    minSubtotal: String(promo.minSubtotal ?? 0),
    maxDiscount: promo.maxDiscount === null ? '' : String(promo.maxDiscount),
    startsAt: toDateInput(promo.startsAt),
    endsAt: toDateInput(promo.endsAt),
    firstOrderOnly: Boolean(promo.firstOrderOnly),
    usageLimit: promo.usageLimit === null ? '' : String(promo.usageLimit),
    perUserLimit: String(promo.perUserLimit ?? 1),
    active: promo.active !== false,
  };
}

function payloadFromForm(form) {
  return {
    ...form,
    code: form.code.trim().toUpperCase(),
    discountValue: Number(form.discountValue),
    minSubtotal: Number(form.minSubtotal || 0),
    maxDiscount: form.maxDiscount === '' ? null : Number(form.maxDiscount),
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    usageLimit: form.usageLimit === '' ? null : Number(form.usageLimit),
    perUserLimit: Number(form.perUserLimit || 1),
  };
}

export default function AdminPromosPage() {
  const { token } = useContext(AuthContext);
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadPromos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/promos', { headers: { 'x-auth-token': token } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Promo codes could not be loaded.');
      setPromos(Array.isArray(data.promos) ? data.promos : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) loadPromos(); }, [token, loadPromos]);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(editingId ? `/api/admin/promos/${editingId}` : '/api/admin/promos', {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify(payloadFromForm(form)),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Promo code could not be saved.');
      setMessage(`${data.promo.code} saved.`);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadPromos();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-promos-page container-fluid py-4">
      <div className="admin-cms-page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="admin-about-page__eyebrow">Checkout incentives</p>
          <h2 className="brand-font m-0">Promo codes</h2>
          <p className="text-muted mb-0">Create first-order or general codes. Discounts apply after product pricing and before shipping.</p>
        </div>
        <button type="button" className="btn btn-outline-custom" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); }}>New code</button>
      </div>

      {message && <div className="alert alert-success" role="status">{message}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <form className="admin-promo-form border rounded bg-card p-4" onSubmit={handleSubmit}>
            <h3 className="h5 mb-3">{editingId ? 'Edit promo code' : 'Create promo code'}</h3>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">Code</label>
                <input className="form-control" value={form.code} onChange={(event) => setField('code', event.target.value.toUpperCase())} maxLength={50} required />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Discount type</label>
                <select className="form-select" value={form.discountType} onChange={(event) => setField('discountType', event.target.value)}>
                  <option value="percent">Percentage</option>
                  <option value="fixed">Fixed amount (₹)</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Discount value</label>
                <input className="form-control" type="number" min="0.01" step="0.01" value={form.discountValue} onChange={(event) => setField('discountValue', event.target.value)} required />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Minimum subtotal</label>
                <input className="form-control" type="number" min="0" step="0.01" value={form.minSubtotal} onChange={(event) => setField('minSubtotal', event.target.value)} />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label">Max discount</label>
                <input className="form-control" type="number" min="0.01" step="0.01" value={form.maxDiscount} onChange={(event) => setField('maxDiscount', event.target.value)} placeholder="None" />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Starts at</label>
                <input className="form-control" type="datetime-local" value={form.startsAt} onChange={(event) => setField('startsAt', event.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Ends at</label>
                <input className="form-control" type="datetime-local" value={form.endsAt} onChange={(event) => setField('endsAt', event.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Total usage limit</label>
                <input className="form-control" type="number" min="1" step="1" value={form.usageLimit} onChange={(event) => setField('usageLimit', event.target.value)} placeholder="Unlimited" />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Uses per customer</label>
                <input className="form-control" type="number" min="1" step="1" value={form.perUserLimit} onChange={(event) => setField('perUserLimit', event.target.value)} required />
              </div>
              <div className="col-12 d-flex flex-wrap gap-3">
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={form.firstOrderOnly} onChange={(event) => setField('firstOrderOnly', event.target.checked)} />
                  <span className="form-check-label">First order only</span>
                </label>
                <label className="form-check">
                  <input className="form-check-input" type="checkbox" checked={form.active} onChange={(event) => setField('active', event.target.checked)} />
                  <span className="form-check-label">Active</span>
                </label>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-4">
              <button className="btn btn-primary-custom" type="submit" disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save changes' : 'Create code'}</button>
              {editingId && <button className="btn btn-outline-custom" type="button" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); }}>Cancel</button>}
            </div>
          </form>
        </div>

        <div className="col-12 col-xl-7">
          {loading ? <div role="status" className="text-muted">Loading promo codes…</div> : promos.length === 0 ? (
            <div className="border rounded bg-card p-4 text-muted">No promo codes yet.</div>
          ) : (
            <div className="admin-promos-list d-grid gap-3">
              {promos.map((promo) => (
                <article key={promo.id} className="border rounded bg-card p-4">
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <h3 className="h5 mb-0">{promo.code}</h3>
                        <span className={`badge ${promo.active ? 'bg-success' : 'bg-secondary'}`}>{promo.active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <p className="text-muted mb-2">{promo.discountType === 'percent' ? `${promo.discountValue}% off` : `₹${promo.discountValue.toFixed(2)} off`} · {promo.firstOrderOnly ? 'First order only' : 'All customers'}</p>
                      <p className="small mb-0">Used {promo.usageCount}{promo.usageLimit ? ` of ${promo.usageLimit}` : ''} · {promo.perUserLimit} per customer · Minimum ₹{promo.minSubtotal.toFixed(2)}</p>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-custom" onClick={() => { setForm(formFromPromo(promo)); setEditingId(promo.id); }}>Edit</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
